/**
 * What a script's operations add up to (D45): how long a step takes where
 * its finish bounds it, and how much each pump moves from the bag feeding it
 * to the first bag or chamber it reaches. Arithmetic on what the author
 * commanded, never physics: where a pressure or vacuum moves liquid, the
 * volumes it touches become unknown, because only the solver could say.
 */
import type { DesignDocument, EdgeDocument, NodeDocument, TemplateDocument } from "../design/document";
import type { Action, ConditionSetDocument, RunPump, RunSetpoint, RunSource, ScriptDocument, Step } from "../design/protocol";
import { AIR } from "../design/protocol";
import { flowIn, TUBING_SET_UNITS, volumeIn, type LiquidUnits } from "../workspace/units";
import { parameterValue, passes, type StepPreview } from "./preview";
import { profileSeconds, profileValues, secondsText } from "./profiles";

export { profileLevels, profilePeak, profileReaches, profileSeconds, profileValues, secondsText } from "./profiles";

// --- One operation -------------------------------------------------------------

/** Whether it finishes by itself: a dispense, a timed run, a ramp, a source's or a regulator's profile. */
export function completes(operation: RunPump | RunSource | RunSetpoint): boolean {
  if (operation.kind !== "pump") return true;
  return operation.mode === "volume" || operation.mode === "time" || operation.mode === "ramp";
}

/** The operations in a step that complete: pumps', sources' and regulators' profiles. */
export function completing(actions: readonly Action[]): (RunPump | RunSource | RunSetpoint)[] {
  return actions.filter(
    (action): action is RunPump | RunSource | RunSetpoint =>
      (action.kind === "pump" || action.kind === "source_profile" || action.kind === "setpoint_profile") && completes(action),
  );
}

/** How long it runs, where commanded (seconds). */
export function pumpSeconds(pump: RunPump): number | null {
  if (pump.mode === "volume" && pump.volume && pump.rate) return pump.volume / pump.rate;
  if (pump.mode === "time") return pump.seconds ?? null;
  if (pump.mode === "ramp") return (pump.segments ?? []).reduce((sum, segment) => sum + segment.seconds, 0);
  return null;
}

/** How much it moves, where commanded (m³): the area under its rate. Open-ended runs need the step's time. */
export function pumpVolume(pump: RunPump, stepSeconds: number | null = null): number | null {
  switch (pump.mode) {
    case "off":
      return 0;
    case "volume":
      return pump.volume ?? null;
    case "time":
      return pump.rate && pump.seconds ? pump.rate * pump.seconds : null;
    case "ramp":
      return (pump.segments ?? []).reduce((sum, segment) => sum + ((segment.from_rate + segment.to_rate) / 2) * segment.seconds, 0);
    case "run":
      return pump.rate && stepSeconds !== null ? pump.rate * stepSeconds : null;
  }
}

/**
 * The script without what it asks of components the design no longer holds:
 * a deleted valve's states, pump's runs, regulator's or mixer's setpoints,
 * in its steps and at step zero. Nothing else could show or remove them.
 * The same script back when nothing refers to a deleted component.
 */
export function withoutDeleted(script: ScriptDocument, design: DesignDocument): ScriptDocument {
  const held = new Set([...(design.nodes ?? []).map((node) => node.local_id), ...(design.thermal_zones ?? []).map((zone) => zone.local_id)]);
  const kept = (action: Action) => !("target" in action) || held.has(action.target);
  const steps = script.steps ?? [];
  const stale =
    (script.initial_states ?? []).some((start) => !held.has(start.target)) ||
    (script.initial_setpoints ?? []).some((start) => !held.has(start.target)) ||
    steps.some((step) => !(step.actions ?? []).every(kept));
  if (!stale) return script;
  return {
    ...script,
    ...(script.initial_states && { initial_states: script.initial_states.filter((start) => held.has(start.target)) }),
    ...(script.initial_setpoints && { initial_setpoints: script.initial_setpoints.filter((start) => held.has(start.target)) }),
    steps: steps.map((step) => (step.actions && !step.actions.every(kept) ? { ...step, actions: step.actions.filter(kept) } : step)),
  };
}

// --- Display -------------------------------------------------------------------

const round = (value: number) => String(Number(value.toPrecision(3)));

/** A rate in the design's flow unit (D49), to three figures: "600 mL/min". */
export function rateText(rate: number, units: LiquidUnits = TUBING_SET_UNITS): string {
  return flowIn(rate, units, 3);
}

/** A volume in the design's volume unit (D49), to three figures: "10,000 mL". */
export function volumeText(volume: number, units: LiquidUnits = TUBING_SET_UNITS): string {
  return volumeIn(volume, units, 3);
}


/** An operation in a few characters, for a table cell: "200 mL @ 600 mL/min", "run 5 mL/min". */
export function operationText(pump: RunPump, units: LiquidUnits = TUBING_SET_UNITS): string {
  const rate = pump.rate ? rateText(pump.rate, units) : "?";
  switch (pump.mode) {
    case "off":
      return "off";
    case "run":
      return `run ${rate}${pump.keep_running ? " →" : ""}`;
    case "volume":
      return `${pump.volume ? volumeText(pump.volume, units) : "?"} @ ${rate}`;
    case "time":
      return `${pump.seconds ? secondsText(pump.seconds) : "?"} @ ${rate}`;
    case "ramp": {
      const volume = pumpVolume(pump);
      return `ramp ×${pump.segments?.length ?? 0}${volume ? ` · ${volumeText(volume, units)}` : ""}`;
    }
  }
}

/** A value as the instrument reads it: kPa for pressure and vacuum, the design's flow unit. */
export function sourceValueText(value: number, quantity: RunSource["quantity"], units: LiquidUnits = TUBING_SET_UNITS): string {
  return quantity === "flow" ? rateText(value, units) : `${round(value / 1000)} kPa`;
}

/** A profile in the fewest characters, for a chip or a cell: "150→350 kPa", "ramp →100 kPa". */
export function profileShort(profile: RunSource | RunSetpoint, units: LiquidUnits = TUBING_SET_UNITS): string {
  const values = profileValues(profile);
  if (values.length === 0) return profile.mode;
  const last = sourceValueText(values.at(-1)!, profile.quantity, units);
  if (profile.mode === "ramp") return `ramp →${last}`;
  const first = profile.quantity === "flow" ? sourceValueText(values[0]!, profile.quantity, units) : String(round(values[0]! / 1000));
  return `${first}→${last}`;
}

/** A profile in a few characters: "150→350 kPa by 10 kPa, 10 s each", "ramp ×2 to 100 kPa". */
export function profileText(profile: RunSource | RunSetpoint, units: LiquidUnits = TUBING_SET_UNITS): string {
  const text = (value: number) => sourceValueText(value, profile.quantity, units);
  if (profile.mode === "stairs") {
    const { start, end, increment, hold } = profile;
    if (start == null || end == null || !increment) return "stairs ?";
    const unit = profile.quantity === "flow" ? "" : " kPa";
    const bare = (value: number) => (profile.quantity === "flow" ? text(value) : String(round(value / 1000)));
    return `${bare(start)}→${bare(end)}${unit} by ${text(increment)}${hold ? `, ${secondsText(hold)} each` : ""}`;
  }
  const last = profile.segments?.at(-1);
  return `ramp ×${profile.segments?.length ?? 0}${last ? ` to ${text(last.to_value)}` : ""}`;
}

// --- A step's time ----------------------------------------------------------------

export interface StepTime {
  /** Seconds, where known or bounded. */
  seconds: number | null;
  /** `exact`: it will take this; `most`: at most this (a sensor or the operator may end it sooner); `unknown`. */
  bound: "exact" | "most" | "unknown";
}

/** How long a step takes: the earliest of what bounds it, and whether a condition may end it sooner. */
export function stepTime(step: Step): StepTime {
  const finish = step.finish ?? {};
  const times = completing(step.actions ?? []).map((operation) =>
    operation.kind === "pump" ? pumpSeconds(operation) : profileSeconds(operation),
  );
  const done = finish.complete && times.length > 0 && times.every((time) => time !== null) ? Math.max(...(times as number[])) : null;
  const bounds = [done, finish.max_seconds ?? null].filter((time): time is number => time !== null);
  if (bounds.length === 0) return { seconds: null, bound: "unknown" };
  const seconds = Math.min(...bounds);
  return { seconds, bound: (finish.conditions ?? []).length > 0 ? "most" : "exact" };
}

export function stepTimeText(time: StepTime): string {
  if (time.seconds === null) return "?";
  return `${time.bound === "most" ? "≤ " : ""}${secondsText(time.seconds)}`;
}

/** A step's finish in a few words: "20 s", "B1 ≥ 1 or 30 min (fail)". */
export function finishText(
  step: Step,
  names: Readonly<Record<string, string>> = {},
  units: LiquidUnits = TUBING_SET_UNITS,
): string {
  const finish = step.finish ?? {};
  const parts: string[] = [];
  if (finish.complete) parts.push("done");
  for (const condition of finish.conditions ?? []) {
    const trigger =
      condition.kind === "sensed" || condition.kind === "pressure_threshold"
        ? `${condition.edge ? " on edge" : ""}${condition.hold ? ` hold ${secondsText(condition.hold)}` : ""}${condition.ignore ? ` ignore <${secondsText(condition.ignore)}` : ""}`
        : "";
    const cmp = (c: string) => ({ lt: "<", le: "≤", gt: ">", ge: "≥" })[c] ?? c;
    if (condition.kind === "sensed") {
      const measured = { reading: "", change: " change", drop: " drop" }[condition.measure ?? "reading"];
      parts.push(`${names[condition.sensor] ?? condition.sensor}${measured} ${cmp(condition.comparison)} ${condition.value}${trigger}`);
    }
    else if (condition.kind === "pressure_threshold")
      parts.push(`${names[condition.at] ?? condition.at} ${cmp(condition.comparison)} ${round(condition.pressure / 1000)} kPa${trigger}`);
    else if (condition.kind === "volume_delivered") parts.push(`${volumeText(condition.volume, units)} delivered`);
    else if (condition.kind === "vessel_empty") parts.push(`${names[condition.vessel] ?? condition.vessel} empty`);
    else parts.push("operator");
  }
  if (finish.max_seconds) parts.push(`${secondsText(finish.max_seconds)}${finish.on_max === "fail" ? " (fail)" : ""}`);
  return parts.join(" or ") || "?";
}

// --- Volumes ------------------------------------------------------------------------

/** What holds a volume the arithmetic follows: bags and chambers. */
const isVessel = (node: NodeDocument) => node.role === "reservoir" || node.role === "chamber" || node.role === "variable_volume_chamber";

const flowPorts = (node: NodeDocument) => (node.ports ?? []).filter((port) => port.kind !== "instrument" && port.kind !== "vent").map((port) => port.local_id);

/**
 * The first vessel liquid can reach from `start` (a port ref), through lines,
 * the parts mounted on them that pass, and components that pass in `states`,
 * never back through `through` (the pump). Null if none, or more than one at
 * the same distance: the arithmetic does not guess.
 *
 * Downstream, the way liquid goes, or upstream, where it comes from: either
 * way a running pump is passed only as it pumps, inlet to outlet, since
 * nothing goes back through an occluded head.
 */
function reach(
  design: DesignDocument,
  states: Readonly<Record<string, string>>,
  start: string | readonly string[],
  through: string | readonly string[],
  downstream: boolean,
): string | null {
  const nodes = new Map((design.nodes ?? []).map((node) => [node.local_id, node]));
  const mountedOn = new Map<string, NodeDocument[]>();
  for (const node of design.nodes ?? []) if (node.mount) mountedOn.set(node.mount.edge, [...(mountedOn.get(node.mount.edge) ?? []), node]);
  // Along a line from its start to its end: past a running head mounted on it only that way.
  const open = (edge: EdgeDocument, forwards: boolean) =>
    (mountedOn.get(edge.local_id) ?? []).every(
      (part) => passes(part, states[part.local_id]) && (!pumping(part) || forwards === downstream),
    );
  const pumping = (node: NodeDocument) => node.role === "pump" && passes(node, states[node.local_id]);
  const inletOf = (node: NodeDocument) => {
    const ports = flowPorts(node);
    return ports.find((port) => /in/.test(port)) ?? ports[0];
  };
  const byPort = new Map<string, EdgeDocument[]>();
  for (const edge of design.edges ?? []) {
    for (const end of [edge.from_port, edge.to_port]) byPort.set(end, [...(byPort.get(end) ?? []), edge]);
  }
  const seen = new Set<string>(typeof through === "string" ? [through] : through);
  let frontier = typeof start === "string" ? [start] : [...start];
  while (frontier.length > 0) {
    const found = new Set<string>();
    const next: string[] = [];
    for (const port of frontier) {
      for (const edge of byPort.get(port) ?? []) {
        if (!open(edge, edge.from_port === port)) continue;
        const other = edge.from_port === port ? edge.to_port : edge.from_port;
        const id = other.split(".")[0]!;
        if (seen.has(id)) continue;
        seen.add(id);
        const node = nodes.get(id);
        if (!node) continue;
        if (isVessel(node)) {
          found.add(id);
          continue;
        }
        if (node.role === "vent" || !passes(node, states[id])) continue;
        // Met at its outlet going downstream, or at its inlet going upstream, a running pump is against the flow.
        if (pumping(node) && !node.mount && (other === `${id}.${inletOf(node)}`) !== downstream) continue;
        for (const local of flowPorts(node)) if (`${id}.${local}` !== other) next.push(`${id}.${local}`);
      }
    }
    if (found.size === 1) return [...found][0]!;
    if (found.size > 1) return null;
    frontier = next;
  }
  return null;
}

/** A pump's feed and delivery: the vessel on its inlet's side and on its outlet's. */
function pumpEnds(design: DesignDocument, states: Readonly<Record<string, string>>, pump: string): { from: string | null; to: string | null } {
  const node = design.nodes?.find((candidate) => candidate.local_id === pump);
  if (node?.mount) {
    // A head mounted on its tube (D40, D48) has no lines of its own: it pumps
    // from the tube's start towards its end, as the tube's segments run.
    const edge = design.edges?.find((candidate) => candidate.local_id === node.mount!.edge);
    return edge
      ? { from: beyondMount(design, states, edge, node, "from"), to: beyondMount(design, states, edge, node, "to") }
      : { from: null, to: null };
  }
  const ports = node ? flowPorts(node) : [];
  const inlet = ports.find((port) => /in/.test(port)) ?? ports[0];
  const outlet = ports.find((port) => port !== inlet);
  return {
    from: inlet ? reach(design, states, `${pump}.${inlet}`, pump, false) : null,
    to: outlet ? reach(design, states, `${pump}.${outlet}`, pump, true) : null,
  };
}

/**
 * The first vessel beyond one end of the tube a pump is mounted on: through
 * any other parts mounted between the pump and that end, then on from the
 * part the tube ends at. Null where the way is shut or unclear.
 */
function beyondMount(
  design: DesignDocument,
  states: Readonly<Record<string, string>>,
  edge: EdgeDocument,
  pump: NodeDocument,
  side: "from" | "to",
): string | null {
  const at = pump.mount!.distance;
  const between = (design.nodes ?? []).filter(
    (part) =>
      part !== pump &&
      part.mount?.edge === edge.local_id &&
      (side === "from" ? part.mount.distance < at : part.mount.distance > at),
  );
  if (!between.every((part) => passes(part, states[part.local_id]))) return null;
  const end = side === "from" ? edge.from_port : edge.to_port;
  const id = end.split(".")[0]!;
  const node = design.nodes?.find((candidate) => candidate.local_id === id);
  if (!node) return null;
  if (isVessel(node)) return id;
  if (node.role === "vent" || !passes(node, states[id])) return null;
  const onward = flowPorts(node)
    .map((port) => `${id}.${port}`)
    .filter((port) => port !== end);
  return reach(design, states, onward, [pump.local_id, id], side === "to");
}

/** What a line holds full (m³): its bore, or width and depth, over its length. Null without them. */
export function lineVolume(edge: EdgeDocument): number | null {
  const { length, width, depth, cross_section } = edge.hydraulic ?? {};
  if (!length || !width) return null;
  const area = cross_section === "rectangular" || cross_section === "trapezoidal" ? width * (depth ?? width) : Math.PI * (width / 2) ** 2;
  return area * length;
}

/**
 * The liquid a step's lines and parts took up (m³): what they hold wet at its
 * end less what they held before it, summed by `key` (a region, or a side of
 * a pump). Null for a key where a line that changed has no bore or length.
 */
function holdUpChange(
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>>,
  before: StepPreview,
  after: StepPreview,
  key: (element: string, span?: number) => string | undefined,
): Map<string, number | null> {
  const change = new Map<string, number | null>();
  const add = (at: string | undefined, volume: number | null) => {
    if (at === undefined) return;
    const now = change.get(at);
    change.set(at, now === null || volume === null ? null : (now ?? 0) + volume);
  };
  for (const edge of design.edges ?? []) {
    const full = lineVolume(edge);
    (after.edges[edge.local_id] ?? []).forEach((span, index) => {
      const was = before.edges[edge.local_id]?.[index]?.liquid ?? null;
      const wet = (span.liquid !== null ? 1 : 0) - (was !== null ? 1 : 0);
      if (wet === 0) return;
      add(key(edge.local_id, index), full === null ? null : wet * full * (span.to - span.from));
    });
  }
  for (const node of design.nodes ?? []) {
    if (isVessel(node)) continue;
    const wet = (after.nodes[node.local_id]?.liquid ? 1 : 0) - (before.nodes[node.local_id]?.liquid ? 1 : 0);
    if (wet === 0) continue;
    add(key(node.local_id), wet * (parameterValue(node, templates, "hold_up_volume") ?? 0));
  }
  return change;
}

/**
 * The lines and parts on one side of a pump (its inlet's or its outlet's),
 * as far as the vessels there and whatever is shut: where what it moves
 * fills the lines on the way.
 */
function pumpSide(design: DesignDocument, states: Readonly<Record<string, string>>, pump: string, port: string): Set<string> {
  const nodes = new Map((design.nodes ?? []).map((node) => [node.local_id, node]));
  const mountedOn = new Map<string, NodeDocument[]>();
  for (const node of design.nodes ?? []) if (node.mount) mountedOn.set(node.mount.edge, [...(mountedOn.get(node.mount.edge) ?? []), node]);
  const byPort = new Map<string, EdgeDocument[]>();
  for (const edge of design.edges ?? []) for (const end of [edge.from_port, edge.to_port]) byPort.set(end, [...(byPort.get(end) ?? []), edge]);
  const side = new Set<string>();
  const seen = new Set<string>([pump]);
  let frontier = [`${pump}.${port}`];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const at of frontier) {
      for (const edge of byPort.get(at) ?? []) {
        if (side.has(edge.local_id)) continue;
        side.add(edge.local_id);
        const parts = mountedOn.get(edge.local_id) ?? [];
        for (const part of parts) side.add(part.local_id);
        if (!parts.every((part) => passes(part, states[part.local_id]))) continue;
        const other = edge.from_port === at ? edge.to_port : edge.from_port;
        const id = other.split(".")[0]!;
        if (seen.has(id)) continue;
        seen.add(id);
        const node = nodes.get(id);
        if (!node || isVessel(node) || node.role === "vent") continue;
        side.add(id);
        if (!passes(node, states[id])) continue;
        for (const local of flowPorts(node)) if (`${id}.${local}` !== other) next.push(`${id}.${local}`);
      }
    }
    frontier = next;
  }
  return side;
}

/**
 * Each vessel's volume at step zero and after each step (m³), or null where
 * it is not known. `columns` are the previews: step zero, then each step.
 *
 * What leaves a vessel reaches the next less what the lines and parts on the
 * way take up (their hold-up), and what gas sweeps out of the lines is added
 * to the vessel it collects in (D60). A pump delivers what it is set to, less
 * what its outlet's lines take up; its feed gives that and what its inlet's
 * lines take up. A step that runs a vessel dry past an air detector moves all
 * it held.
 */
export function trackVolumes(
  design: DesignDocument,
  conditions: ConditionSetDocument,
  script: ScriptDocument,
  columns: readonly StepPreview[],
  templates: Readonly<Record<string, TemplateDocument>> = {},
): Record<string, number | null>[] {
  const vessels = (design.nodes ?? []).filter(isVessel).map((node) => node.local_id);
  const start = columns[0];
  if (!start) return [];
  const first: Record<string, number | null> = {};
  for (const id of vessels) {
    const fill = conditions.initial_fill?.find((entry) => entry.target === id);
    const liquid = start.nodes[id]?.liquid;
    // A vessel starts with what the set-up says, or empty; after a parent the preview tracks none.
    // Air is no liquid, whatever volume the set-up gives it.
    first[id] = start.inherited ? null : fill?.fluid === AIR ? 0 : typeof fill?.volume === "number" ? fill.volume : liquid ? null : 0;
  }
  const out = [first];
  (script.steps ?? []).forEach((step, index) => {
    const column = columns[index + 1];
    const now = { ...out[out.length - 1]! };
    if (!column) {
      out.push(now);
      return;
    }
    const holdUp = holdUpChange(design, templates, columns[index]!, column, (element, span) =>
      column.regions?.[span === undefined ? element : `${element}:${span}`],
    );
    // Each region's hold-up is taken once, by the first vessel that receives in it.
    const charged = new Set<string>();
    const takenUp = (vessel: string): number | null => {
      const region = column.regions?.[vessel];
      if (region === undefined || charged.has(region)) return 0;
      charged.add(region);
      return holdUp.get(region) ?? 0;
    };
    const receive = (vessel: string, amount: number | null) => {
      const before = now[vessel];
      const lost = takenUp(vessel);
      now[vessel] = amount === null || lost === null || before === null || before === undefined ? null : Math.max(0, before + amount - lost);
    };
    const accounted = new Set<string>();
    // Run dry past an air detector: all it held went on. Two pumps may feed or
    // fill one vessel, each its own share; a run-out's vessel is settled.
    const ranOut = new Set<string>();
    for (const { from, to } of column.ranOut ?? []) {
      const held = now[from] ?? null;
      now[from] = 0;
      accounted.add(from);
      ranOut.add(from);
      if (to) {
        receive(to, held);
        accounted.add(to);
        ranOut.add(to);
      }
    }
    const time = stepTime(step);
    for (const action of step.actions ?? []) {
      if (action.kind !== "pump" || action.mode === "off") continue;
      const { from, to } = pumpEnds(design, column.states, action.target);
      if ((from && ranOut.has(from)) || (to && ranOut.has(to))) continue;
      const moved = pumpVolume(action, time.bound === "exact" ? time.seconds : null);
      // What it moves is what leaves its outlet: the lines it fills before it
      // come from the feed as well, those after it short the delivery.
      const pump = design.nodes?.find((node) => node.local_id === action.target);
      const ports = pump && !pump.mount ? flowPorts(pump) : [];
      const inlet = ports.find((port) => /in/.test(port)) ?? ports[0];
      const outlet = ports.find((port) => port !== inlet);
      const sides =
        inlet && outlet
          ? { inlet: pumpSide(design, column.states, action.target, inlet), outlet: pumpSide(design, column.states, action.target, outlet) }
          : null;
      const onSide = sides
        ? holdUpChange(design, templates, columns[index]!, column, (element) =>
            sides.outlet.has(element) ? "outlet" : sides.inlet.has(element) ? "inlet" : undefined,
          )
        : null;
      if (sides) for (const region of [column.regions?.[from ?? ""], column.regions?.[to ?? ""]]) if (region !== undefined) charged.add(region);
      if (from) {
        const before = now[from];
        const drawn = onSide ? (onSide.get("inlet") ?? 0) : 0;
        now[from] = moved === null || drawn === null || before === null || before === undefined ? null : Math.max(0, before - moved - drawn);
        accounted.add(from);
      }
      if (to) {
        const before = now[to];
        const lost = onSide ? (onSide.get("outlet") ?? 0) : takenUp(to);
        now[to] = moved === null || lost === null || before === null || before === undefined ? null : Math.max(0, before + moved - lost);
        accounted.add(to);
      }
    }
    // Swept out of the lines by gas: into the vessel it collects in.
    for (const id of column.collected ?? []) {
      if (accounted.has(id) || !vessels.includes(id)) continue;
      receive(id, 0);
      accounted.add(id);
    }
    // Moved by pressure or vacuum, or by a pump the arithmetic cannot place: unknown.
    for (const id of column.moved ?? []) if (vessels.includes(id) && !accounted.has(id)) now[id] = null;
    out.push(now);
  });
  return out;
}
